// Demo data for /features/setup-assistant: a short, believable run of the
// setup assistant (the admin's words, the assistant's question, the draft it
// fills in, the check page's step and the done page's lines) and a short Tappy
// chat, in the shapes the real components read. Nothing here calls the API or
// is a real club. The words and fields are the product's own: a new box league
// needs its first cycle's start and end dates (the API's cycleStartsOn /
// cycleEndsOn, SetupDraftAllowlist.CycleFields), and the check page names them
// "First cycle starts" / "First cycle ends" (utils/setupAssistant FIELD_WORDS).
import {ENTITY, STEP_ACTION} from '~/utils/setupAssistant';

export const SETUP_DEMO_CLUB = Object.freeze({id: '00000000-0000-0000-0000-000000000001', name: 'Riverside Squash Club'});

/** What the admin types: one sentence, the whole competition. */
export const SETUP_DEMO_ASK = 'I want to run a winter box league for squash, singles, 4 per box';

/** The assistant's opening (a conversation about competitions goes straight to the point). */
export const SETUP_DEMO_OPENING = `Hi. I'll set up ${SETUP_DEMO_CLUB.name} with you. Tell me about the competition in plain words, and I'll ask only what changes the outcome.`;

/** yyyy-MM-dd of the next Monday at least a few days away, plus `weeks` × 7 − 1 days for the Sunday it ends on. */
export function setupDemoCycle(weeks, now = new Date()) {
  const start = new Date(now);
  start.setHours(12, 0, 0, 0);
  const ahead = (8 - start.getDay()) % 7 || 7;
  start.setDate(start.getDate() + ahead);
  const end = new Date(start);
  end.setDate(end.getDate() + weeks * 7 - 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  const word = (d) => d.toLocaleDateString('en-GB', {weekday: 'short', day: 'numeric', month: 'short'});
  return {startsOn: iso(start), endsOn: iso(end), label: `Starts ${word(start)}, ends ${word(end)}`, weeks};
}

/** The one question that changes the outcome: the first cycle's dates (SetupQuestionCard's `question`). */
export function setupDemoQuestion(now = new Date()) {
  return {
    id: 'q-first-cycle',
    text: 'When should the first cycle start and end?',
    why: "A new box league needs its first cycle's start and end dates. Players join the cycle by its link.",
    multiSelect: false,
    choices: [4, 6, 8].map((weeks) => {
      const c = setupDemoCycle(weeks, now);
      return {label: c.label, handle: String(weeks), line: `${weeks} weeks${weeks === 6 ? ': the usual, time for four matches at your own pace' : weeks === 4 ? ': a fresh box every month' : ': a long round for a quiet winter'}`};
    }),
  };
}

/** The assistant's reply after the pick, before the draft is ready. */
export const setupDemoReply = (cycle) => `Done. A singles box league for squash, boxes of 4, scored with the club's squash system (best of 5 to 11). Its first cycle ${cycle.label.replace(/^Starts/, 'starts')} (${cycle.weeks} weeks), and players join it by its invite link. Your draft is ready: review it, and nothing is created until you press Create setup.`;

/** The draft, before and after the first cycle is known (SetupDraftDock / SetupDraftPanel / SetupStepCard's `proposal` and `step`). */
export function setupDemoProposal({cycle = null} = {}) {
  const league = {
    id: 'step-league', action: STEP_ACTION.new, entityType: ENTITY.league, section: 'competitions',
    label: 'Winter Box League',
    summary: cycle
        ? `Squash · singles · boxes of 4 · first cycle ${cycle.label.replace(/^Starts/, 'starts')} · scored best of 5 to 11 · open to joining by its invite link`
        : 'Squash · singles · boxes of 4 · open to joining by its invite link',
    after: {
      name: 'Winter Box League', sportId: 'sport-squash', playersPerTeam: 1, defaultDivisionSize: 4, scoringSystemId: 'scoring-squash',
      cycleName: 'Winter 2026', cycleStartsOn: cycle?.startsOn ?? null, cycleEndsOn: cycle?.endsOn ?? null,
    },
    errors: [],
  };
  return {
    id: 'demo-proposal', status: cycle ? 'Ready' : 'Draft', ready: !!cycle,
    steps: [league],
    assumptions: [
      {id: 'a-scoring', text: "Scoring: the club's squash system, best of 5 games to 11."},
      {id: 'a-joining', text: 'Joining: anyone with the invite link, placed in a box by you.'},
    ],
    unsupported: [], errors: [], beforeYouCanCharge: [],
  };
}

/** The names the check page shows for ids in a step (SetupStepCard's `ctx`). */
export const SETUP_DEMO_CTX = Object.freeze({names: {'sport-squash': 'Squash', 'scoring-squash': 'Squash · best of 5 to 11'}, currency: 'gbp'});

/** What the done page says was created. */
export const SETUP_DEMO_CREATED = Object.freeze([
  '1 box league created, open to joining by its invite link',
]);

/** The done page's next steps for this run. */
export const SETUP_DEMO_NEXT = Object.freeze([
  {n: 1, text: 'Share the invite link, or the printable QR poster, with your players.', button: 'Share'},
  {n: 2, text: 'Once a few have joined, open the league and place them in boxes.', button: 'Your competitions'},
]);

// ── Tappy, our AI helper ──

/** A question the setup assistant can really draft (a new ladder), so Tappy's hand-over is the real one. */
export const TAPPY_DEMO_QUESTION = 'How do I set up a doubles ladder for our club?';

/**
 * Tappy's reply: markdown, as the real chat renders it (bold labels, numbered steps). The steps are the Competitions
 * page's own (docs/features/clubs/competitions-page.md, ladders/ladder-settings.md): New competition → New ladder → the
 * Create ladder wizard, Players per team 2 (Doubles) on its Format step, Create ladder.
 */
export const TAPPY_DEMO_REPLY = `From the club's admin area:

1. Open **Manage competitions** in the admin menu and press **New competition**, then **New ladder**.
2. The **Create ladder** wizard asks **The basics** (name and sport), then on the **Format** step set **Players per team** to **2 (Doubles)**. Tick **Allow players in multiple teams** if someone may play with different partners.
3. The **Challenges** step sets how far up a challenge can reach and how long it stays open.
4. Press **Create ladder**. You land on its **Players** tab to add the first players, and its invite link is in **Invite settings**.

If you would rather describe it in plain words, the setup assistant drafts the ladder for you to check and apply.`;
